import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import { attemptOpenApp, getMobileStoreUrl, MOBILE_STORES } from '../utils/mobileAppRedirect';
import '../styles/deeplink.css';

/**
 * Deep link landing page for product sharing
 * Detects if app is installed and redirects accordingly
 * URL pattern: /produit/:uuid or /p/:uuid
 */
const ProductDeepLink = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [detecting, setDetecting] = useState(true);
    const [productData, setProductData] = useState(null);

    useEffect(() => {
        if (!uuid) {
            navigate('/dashboard');
            return;
        }

        // Fetch product data for metadata
        const fetchProductData = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/products/view/${uuid}/`);
                if (response.ok) {
                    const data = await response.json();
                    setProductData(data);
                }
            } catch (error) {
                console.error('Error fetching product data:', error);
            }
        };

        fetchProductData();

        // Attempt to detect and redirect to app
        let isMounted = true;
        const attemptAppRedirect = async () => {
            const deepLink = `wennze://produit/${uuid}`;
            const webFallback = `/products/view/${uuid}`;
            const storeFallback = getMobileStoreUrl();

            const opened = await attemptOpenApp({
                deepLink,
                webFallbackPath: webFallback,
            });

            if (!isMounted || opened) return;

            setDetecting(false);
            setTimeout(() => {
                if (storeFallback) {
                    window.location.replace(storeFallback);
                    return;
                }
                navigate(webFallback);
            }, 900);
        };

        attemptAppRedirect();

        return () => {
            isMounted = false;
        };
    }, [uuid, navigate]);

    return (
        <div className="deeplink-container">
            <div className="deeplink-content">
                {detecting ? (
                    <>
                        <div className="deeplink-spinner"></div>
                        <h2>Ouverture de Wennze...</h2>
                        <p>Nous ouvrons ce produit dans l'application. Si elle n'est pas installée, vous serez redirigé vers le store adapté à votre téléphone.</p>
                    </>
                ) : (
                    <>
                        <div className="deeplink-icon">W</div>
                        <h2>Application non détectée</h2>
                        <p>{getMobileStoreUrl() ? 'Redirection vers la page de téléchargement...' : 'Vous pouvez consulter ce produit sur le web.'}</p>
                        {productData && (
                            <div className="product-preview">
                                <h3>{productData.name}</h3>
                                <p>{productData.price} {productData.currency}</p>
                            </div>
                        )}
                    </>
                )}

                <div className="deeplink-links">
                    <a
                        href={`/products/view/${uuid}`}
                        className="web-link"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(`/products/view/${uuid}`);
                        }}
                    >
                        Ouvrir dans le navigateur
                    </a>
                    <div className="app-download-links">
                        <p>Télécharger Wennze :</p>
                        <div className="store-buttons">
                            <a
                                href={MOBILE_STORES.ios}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="store-button ios-button"
                            >
                                App Store
                            </a>
                            <a
                                href={MOBILE_STORES.android}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="store-button android-button"
                            >
                                Google Play
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDeepLink;
